import type { Bundle } from 'fhir/r4';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import React from 'react';
import { Loader, Message } from 'semantic-ui-react';
import useSWR from 'swr';
import type { ReferralSummary } from 'types';
import type { PractitionerEntry, ResourceEntry } from 'utils/bundleNarrowing';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher from '../../../data/FHIRFetcher';

import CarePlanSteps from './steps';
import Tasks from './tasks';
import type { StepMutate, StepPatientIdentity } from './types';

/**
 * Props for {@link CarePlan}, as passed by `components/patient/index.tsx`.
 *
 * This component owns no patient data of its own — it runs four SWR queries and
 * distributes everything else straight through to `CarePlanSteps` and `Tasks`.
 * The mutators are typed `StepMutate` (returning `void`) because `onRefresh`
 * and the `useImperativeHandle` body call them bare, without chaining.
 */
type CarePlanProps = Omit<StepPatientIdentity, 'orgId'> & {
  /**
   * The payer org. Named `org` here and renamed to `orgId` when forwarded to
   * `CarePlanSteps` and `Tasks`, which is why this cannot simply reuse
   * `StepPatientIdentity`'s `orgId`.
   */
  org?: string | undefined;
  /** The `/Task/?patient=...` bundle, forwarded to the stepper as `.entry`. */
  tasksData?: Bundle | undefined;
  /** SWR's error for the task query; renders a message and blocks the stepper. */
  tasksError?: Error | undefined;
  /** Revalidates the patient-level task bundle. */
  tasksMutate: StepMutate;
  /** Revalidates the member's care team. */
  careTeamMutate: StepMutate;
  /** Re-runs the parent's `/Patient/{id}` query. */
  refreshPatientDetails: StepMutate;
  /** Revalidates the profile observation query. */
  profileMutate: StepMutate;
  /** Revalidates the email/SMS communication log. */
  communicationsMutate: StepMutate;
  /** Opens the survey sidebar for a questionnaire id. */
  toggleSurveySidebar?: ((questionnaireId: string) => void) | undefined;
  /** Active referrals, forwarded to the stepper. */
  referralData?: unknown;
  /** All referrals for the member, forwarded to `Tasks`. */
  allReferralData?: ReferralSummary[] | undefined;
  /** The org's questionnaire bundle, forwarded for the web-survey actions. */
  questionnaireData?: Bundle | undefined;
  /** The member's `/CareTeam` bundle. */
  careTeamData?: Bundle | undefined;
  /**
   * Loading flags OR-ed into `isRefreshing` and passed down to the stepper.
   *
   * Required rather than optional: they are `||`-ed into a single `isRefreshing`
   * that `Launch` feeds to semantic-ui `disabled` props, which reject
   * `undefined` under `exactOptionalPropertyTypes`. Each is SWR 0.5.7's
   * `isValidating` — a plain `boolean`, always passed by `patient/index.tsx`.
   */
  isCareTeamLoading: boolean;
  isRefreshPatientDetailsLoading: boolean;
  isProfileLoading: boolean;
  isCommunicationsLoading: boolean;
};

/** The imperative surface `patient/index.tsx` drives through its `tasksRef`. */
type CarePlanHandle = {
  refreshTasks: () => void;
  loadData: () => void;
};

const CarePlan = forwardRef<CarePlanHandle, CarePlanProps>((props, ref) => {
  const [loading, setLoading] = React.useState(true);
  // `Tasks` is itself a `forwardRef` exposing `refreshTasks`; this ref drives it.
  const tasksRef = useRef<{ refreshTasks: () => void }>();
  const [newPractitionerData, setNewPractitionerData] = useState<PractitionerEntry[]>([]);

  const authInfo = useAuthContext();

  const tasksData = props.tasksData;
  const tasksError = props.tasksError;
  const tasksMutate = props.tasksMutate;

  const onRefresh = () => {
    //setLoading(true);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    carePlanMutate();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    conditionMutate();
    tasksMutate();
    props.careTeamMutate();
    props.refreshPatientDetails();
    props.profileMutate();
    if (tasksRef.current !== undefined) tasksRef.current.refreshTasks();
  };

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    onSuccess: (_data: Bundle | undefined, _key: string, _config: unknown) => {
      setLoading(false);
    },
  };

  // Filter all practitioners that have only 32 digits guid as ID (not email id as GUID)
  const practitionerOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    // SUSPECTED DEFECT — same shape as the practitioner effect in
    // `components/patient/index.tsx`. `Bundle.entry` is `0..*` and genuinely
    // absent when a `/Practitioner` search matches nothing, so
    // `data.entry.filter(...)` throws "Cannot read properties of undefined"
    // inside SWR's `onSuccess`. `Practitioner.id` is likewise `0..1` and
    // `.length` on an absent id throws too. Both are asserted here so the
    // existing throws are preserved exactly; substituting `?? []` would render
    // the care-team and assign-task pickers silently empty, and whether a
    // practitioner-less deployment should degrade or fail loudly is a clinical
    // call, not a typing one.
    // The `as` on the filter result is the one place this file asserts rather
    // than checks. A `/Practitioner?...` search returns only Practitioners, so
    // the claim holds; routing through `isResourceEntry` instead would insert a
    // real `.filter` call that does not exist today, which is a behaviour
    // change rather than an annotation.
    onSuccess: (data: Bundle | undefined, _key: string, _config: unknown) => {
      const activePractitioners = data!.entry!.filter((practitioner) => {
        return practitioner.resource!.id!.length > 30;
      }) as PractitionerEntry[];
      setNewPractitionerData(activePractitioners);
      setLoading(false);
    },
  };

  const { data: planDefData, error: planDefError } = useSWR<Bundle, Error>(
    [`/PlanDefinition?identifier=${props.org}&status=active`, authInfo.token],
    FHIRFetcher as () => Promise<Bundle>,
    options
  );

  const {
    data: carePlanData,
    error: carePlanError,
    mutate: carePlanMutate,
    isValidating: isCarePlanLoading,
  } = useSWR<Bundle, Error>(
    [`/CarePlan/?patient=${props.patientId}`, authInfo.token],
    FHIRFetcher as () => Promise<Bundle>,
    options,
  );

  //TODO: externalize the condition code below
  const {
    data: conditionData,
    error: conditionError,
    mutate: conditionMutate,
    isValidating: isConditionLoading,
  } = useSWR<Bundle, Error>(
    [
      `/Condition/?patient=${props.patientId}&code=733423003&clinical-status=active&_sort=-onset-date&_count=1`,
      authInfo.token,
    ],
    FHIRFetcher as () => Promise<Bundle>,
    options
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: practitionerData, error: practitionerError } = useSWR<Bundle, Error>(
    [
      `/Practitioner?active=true&_count=1000&identifier=https://projectwell.io/fhir/practitioner-types|OA,WA,RD,HC`,
      authInfo.token,
    ],
    FHIRFetcher as () => Promise<Bundle>,
    practitionerOptions
  );

  useImperativeHandle(ref, () => ({
    refreshTasks() {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      carePlanMutate();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      conditionMutate();
      tasksMutate();
      props.careTeamMutate();
      if (tasksRef?.current !== undefined) tasksRef?.current?.refreshTasks();
    },
    loadData() {
      setLoading(true);
    },
  }));

  const isRefreshing =
    isCarePlanLoading ||
    isConditionLoading ||
    props.isCareTeamLoading ||
    props.isRefreshPatientDetailsLoading ||
    props.isProfileLoading ||
    props.isCommunicationsLoading;

  return (
    <>
      {loading && <Loader active inline="centered" />}
      <>
        {planDefError && (
          <Message negative>
            <Message.Header>Oops!. Something went wrong.. </Message.Header>
            <p>{planDefError.message}</p>
          </Message>
        )}
      </>
      <>
        {carePlanError && (
          <Message negative>
            <Message.Header>Oops!. Something went wrong.. </Message.Header>
            <p>{carePlanError.message}</p>
          </Message>
        )}
        {!planDefError &&
          planDefData &&
          !planDefError &&
          !carePlanError &&
          carePlanData &&
          conditionData &&
          !conditionError &&
          tasksData &&
          !tasksError && (
            <CarePlanSteps
              fhirPatient={props.fhirPatient}
              orgId={props.org}
              patientId={props.patientId}
              patientName={props?.patientName}
              patientEmail={props?.patientEmail}
              patientPhone={props?.patientPhone}
              carePlanData={carePlanData}
              planDefData={planDefData}
              conditionData={conditionData}
              // `/Task/?patient=...` is a single-type search, so every entry
              // carries a Task. Asserted rather than filtered: routing through
              // `bundleEntriesOf` would insert a runtime `.filter` that does
              // not exist today and would change what the stepper sorts.
              tasks={tasksData.entry as ResourceEntry<'Task'>[]}
              refresh={onRefresh}
              toggleSurveySidebar={props.toggleSurveySidebar}
              communicationsMutate={props.communicationsMutate}
              referralData={props.referralData}
              PractitionerData={newPractitionerData}
              profileMutate={props.profileMutate}
              questionnaireData={props.questionnaireData}
              careTeamData={props?.careTeamData}
              careTeamMutate={props?.careTeamMutate}
              isRefreshing={isRefreshing}
            />
          )}
      </>
      <>
        {loading && <Loader active inline="centered" />}
        {tasksError && (
          <Message negative>
            <Message.Header>Oops!. Something went wrong.. </Message.Header>
            <p>{tasksError.message}</p>
          </Message>
        )}

        {newPractitionerData && !practitionerError && (
          <Tasks
            orgId={props.org}
            patientId={props.patientId}
            ref={tasksRef}
            practfilter={true}
            PractitionerData={newPractitionerData}
            communicationsMutate={props.communicationsMutate}
            allReferralData={props?.allReferralData}
            fhirPatient={props.fhirPatient}
            patientTasksMutate={tasksMutate}
          />
        )}
      </>
    </>
  );
});

export default CarePlan;
